/**
 * Base Relationship class for all Neo4j relationships
 *
 * Supports flexible construction:
 * - By ID: new HasSteps('thread-123', 'step-456')
 * - By object: new HasSteps(threadObj, stepObj) - validates with Zod
 *
 * Provides CRUD operations:
 * - await rel.save()  // POST to create
 * - await rel.delete() // DELETE to remove
 */

import { z } from 'zod';

export abstract class Relationship<TSource = any, TTarget = any> {
  public readonly sourceId: string;
  public readonly targetId: string;
  public properties: Record<string, any> = {};

  // Cached node references (for future node map integration)
  private _source?: TSource;
  private _target?: TTarget;

  constructor(source: TSource | string, target: TTarget | string, properties?: Record<string, any>) {
    // Handle source
    if (typeof source === 'string') {
      this.sourceId = source;
    } else {
      // Validate against schema and extract ID
      const schema = (this.constructor as any).originSchema;
      const validated = schema ? schema.parse(source) : source;
      this.sourceId = (validated as any).id;
      this._source = source as TSource;
    }

    // Handle target
    if (typeof target === 'string') {
      this.targetId = target;
    } else {
      const schema = (this.constructor as any).destSchema;
      const validated = schema ? schema.parse(target) : target;
      this.targetId = (validated as any).id;
      this._target = target as TTarget;
    }

    // Edge properties
    if (properties) {
      this.properties = properties;
    }
  }

  // Edge property metadata (overridden by generated subclasses)
  static readonly hasEdgeProperties: boolean = false;
  static readonly edgeProperties: Record<string, { type: string; optional: boolean; default: unknown }> = {};

  // Static metadata (overridden by subclasses)
  static get relationshipName(): string {
    throw new Error('Relationship subclass must define static relationshipName');
  }

  static get cardinality(): 'm2m' | 'one2m' | 'one2one' | 'm2one' {
    throw new Error('Relationship subclass must define static cardinality');
  }

  static get origin_property(): string {
    throw new Error('Relationship subclass must define static origin_property');
  }

  static get dest_property(): string {
    throw new Error('Relationship subclass must define static dest_property');
  }

  // Access cached nodes (will integrate with node map later)
  get source(): TSource | undefined {
    if (!this._source) {
      // TODO: Integrate with node map
      // this._source = nodeMap.get(this.sourceId);
    }
    return this._source;
  }

  get target(): TTarget | undefined {
    if (!this._target) {
      // TODO: Integrate with node map
      // this._target = nodeMap.get(this.targetId);
    }
    return this._target;
  }

  /**
   * Save this relationship to the database
   * TODO: Will use NeoNodeBase.save() once proper patching is implemented
   */
  async save(): Promise<void> {
    throw new Error('Relationship.save() not yet implemented - waiting for NeoNodeBase patching');
  }

  /**
   * Delete this relationship from the database
   * TODO: Will use NeoNodeBase.save() once proper patching is implemented
   */
  async delete(): Promise<void> {
    throw new Error('Relationship.delete() not yet implemented - waiting for NeoNodeBase patching');
  }
}

/**
 * BasicRelationship - Concrete implementation for generic relationships
 * Used when no specific relationship class is available
 */
export class BasicRelationship extends Relationship<any, any> {
  static get relationshipName(): string {
    return 'RELATED_TO';
  }

  static get cardinality(): 'm2m' | 'one2m' | 'one2one' | 'm2one' {
    return 'm2m';
  }

  static get origin_property(): string {
    return 'related';
  }

  static get dest_property(): string {
    return 'related_from';
  }
}

/**
 * Type for relationship class constructors with required static properties
 * Used in registries and type checking
 */
export type RelationshipClass = (new (source: any, target: any) => Relationship<any, any>) & {
  readonly relationshipName: string;
  readonly origin_property: string;
  readonly dest_property: string;
  readonly cardinality: 'm2m' | 'one2m' | 'one2one' | 'm2one';
  readonly composition: boolean;
  readonly originModelNames?: readonly string[];
  readonly destModelNames?: readonly string[];
  readonly hasEdgeProperties: boolean;
  readonly edgeProperties?: Record<string, { type: string; optional: boolean; default: unknown }>;
};
